import React, { useState, useEffect, useRef } from "react";
import { Stack, Badge } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import WarningIcon from "@mui/icons-material/Warning";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { TextView } from "../";
import { ASSETS } from "../../utils/config";
import { STATUS } from "../../constants/general";
import { colors } from "../../utils";
import { removeAiWheel, updateAiWheel } from "../../redux/reducers/aiWheels";
import { getAiWheel } from "../../services/api";
import { useQueryClient } from "react-query";

const backgrounds = {
  [STATUS.IN_PROGRESS]: "rgba(252, 183, 32, 0.5)",
  [STATUS.ERROR]: "rgba(255, 102, 102, 0.5)",
  [STATUS.SUCCESS]: "rgba(155, 90, 248, 0.45)",
};

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 12px;
`;

const WheelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WheelCard = styled(Stack)`
  position: relative;
  background-color: #f6f7f9;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  width: 320px;
  background: conic-gradient(
    from 180deg at 50% 50%,
    #f5eede -41.73deg,
    #f7fed5 53.81deg,
    #ccfeff 133.87deg,
    #fdcffe 244.39deg,
    #f5eede 318.27deg,
    #f7fed5 413.81deg
  );
`;

const WheelBackground = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: ${(props) => backgrounds[props.status]};
  z-index: 1;
  border-radius: 8px;
`;

const WheelContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
`;

const ViewButton = styled.div`
  cursor: pointer;
`;

const DeleteButton = styled(CloseIcon)`
  cursor: pointer;
  font-size: 12px;
  color: white;
`;

const ProgressIcon = styled.div`
  position: relative;
  img {
    width: 70px;
    height: 70px;
    object-fit: cover;
    cursor: pointer;
  }
`;

const StyledBadge = styled(Badge)`
  & .MuiBadge-badge {
    background-color: ${colors.PrimaryPurple};
    color: white;
    font-size: 12px;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
  }
`;

const DeleteButtonContainer = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.darkGrey};
  padding: 2px;
  cursor: pointer;
  z-index: 1000;
`;

const WheelItem = ({ wheel, onRemove, onView }) => {
  const [isHovered, setIsHovered] = useState(false);
  const focusListenerRef = useRef(false);
  const intervalRef = useRef(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const callApiForWheel = () => {
    if (wheel.id) {
      getAiWheel(wheel.id)
        .then(response => {
          // Handle the API response here if needed
          console.log('AI Wheel data:', response.data,"FAM");
          dispatch(
            updateAiWheel({
              id: wheel.id,
              ...response.data.data,
              ...response.data.data.extra_data
            })
          );

          // If response has wheel_id, remove all listeners as processing is complete and fetch the gallery wheels again
          if (response.data.data.wheel_id) {
            queryClient.invalidateQueries(["fetchWheels"]);
            // Remove focus listener
            if (focusListenerRef.current) {
              window.removeEventListener('focus', handleFocus);
              focusListenerRef.current = false;
            }
            // Clear interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        })
        .catch(error => {
          console.error('Error fetching AI wheel:', error);
        });
    }
  };

  const handleFocus = () => {
    callApiForWheel();
  };

  useEffect(() => {
    // Only add event listener if it hasn't been added already
    if (!focusListenerRef.current) {
      window.addEventListener('focus', handleFocus);
      focusListenerRef.current = true;
    }

    // Set up interval to call API every 15 seconds
    if (!intervalRef.current) {
      intervalRef.current = setInterval(callApiForWheel, 20000);
    }

    // Cleanup event listener and interval on component unmount
    return () => {
      if (focusListenerRef.current) {
        window.removeEventListener('focus', handleFocus);
        focusListenerRef.current = false;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [wheel.id]);

  console.log({wheel},"FAM")

  return (
    <WheelCard
      key={wheel.id}
      direction="row"
      alignItems="center"
      spacing={2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <WheelBackground status={wheel.status} />
      <WheelContent>
        <div>
          {wheel.status === STATUS.IN_PROGRESS && (
            <CircularProgress
              size={28}
              style={{ color: colors.PrimaryPurple }}
            />
          )}
          {wheel.status === STATUS.ERROR && (
            <WarningIcon style={{ color: colors.error }} />
          )}
          {wheel.status === STATUS.SUCCESS && (
            <AutoAwesomeIcon style={{ color: colors.PrimaryPurple }} />
          )}
        </div>
        <div>
          <TextView
            variant="subtitle"
            style={{
              fontWeight: 600,
              WebkitLineClamp: 1,
              textTransform: "capitalize",
            }}
          >
            {wheel.prompt}
          </TextView>
          <TextView
            style={{ flex: 1, fontSize: "14px", WebkitLineClamp: 2 }}
          >
            {wheel.body}
          </TextView>
        </div>
        <div style={{ position: "relative" }}>
          {wheel.status === STATUS.SUCCESS && (
            <ViewButton
              onClick={() => onView(wheel.id, wheel.wheel_id)}
            >
              <TextView variant="largeText">View</TextView>
            </ViewButton>
          )}
        </div>
      </WheelContent>
      {isHovered && (
        <DeleteButtonContainer>
          <DeleteButton
            fontSize={"small"}
            onClick={(e) => onRemove(wheel.id, e)}
          />
        </DeleteButtonContainer>
      )}
    </WheelCard>
  );
};

const AIWheelProgress = ({ wheels }) => {
  const [toggleState, setToggleState] = useState(false);
  const dispatch = useDispatch();
  const nav = useNavigate();

  const handleToggle = () => setToggleState(!toggleState);

  const handleRemoveWheel = (wheelId, e) => {
    e.stopPropagation();
    dispatch(removeAiWheel({ id: wheelId }));
  };

  const handleViewWheel = (tempId, wheelId) => {
    dispatch(removeAiWheel({ id: tempId }));
    nav(`/wheel/${wheelId}`);
  };

  return (
    <Container>
      {toggleState && (
        <WheelList>
          {wheels.map((wheel) => (
            <WheelItem
              key={wheel.id}
              wheel={wheel}
              onRemove={handleRemoveWheel}
              onView={handleViewWheel}
            />
          ))}
        </WheelList>
      )}
      <StyledBadge
        badgeContent={wheels.length}
        invisible={toggleState}
        max={99}
      >
        <ProgressIcon onClick={handleToggle}>
          <img src={ASSETS.AIWheelProgress1} alt="AI Wheel Progress" />
        </ProgressIcon>
      </StyledBadge>
    </Container>
  );
};

export default AIWheelProgress;
